import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Reusable mobile-nav drawer.
 *
 * Renders a hamburger Button that opens a left-side Sheet containing the full
 * navigation tree. Used by BOTH the consumer TopBar and the admin AdminTopBar so
 * mobile users can reach every route (not just the 5 bottom-nav items in the
 * consumer app, or — for admin — anything at all).
 *
 * Props:
 *   groups: [{ label?, items: [{ to, label, icon, badge?, soon?, end?, disabled?, testid? }] }]
 *   title:  drawer header text (e.g. "CardAssist Online" or "Operations")
 *   subtitle?: small caption under the title
 *   accent?: 'consumer' (default) | 'admin' — drives badge tint
 *   testid?: data-testid base
 */
export const MobileNavDrawer = ({
  groups = [],
  title = 'Navigate',
  subtitle,
  accent = 'consumer',
  testid = 'mobile-nav-drawer',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close when route changes so the drawer doesn't linger.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open navigation"
          className={cn('h-9 w-9 shrink-0 lg:hidden', className)}
          data-testid={`${testid}-trigger`}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-[280px] sm:w-[320px] p-0 flex flex-col bg-[hsl(var(--surface-1))]"
        data-testid={testid}
      >
        <SheetHeader className="px-4 pt-5 pb-3 border-b border-border space-y-1 text-left">
          <SheetTitle className="font-display text-[15px] tracking-tight flex items-center justify-between gap-2">
            <span>{title}</span>
            <SheetClose asChild>
              <button className="text-muted-foreground hover:text-foreground" aria-label="Close navigation">
                <X className="h-4 w-4" />
              </button>
            </SheetClose>
          </SheetTitle>
          {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
          {groups.map((group, gi) => (
            <div key={group.label || `group-${gi}`} className="mb-2">
              {group.label && (
                <div className="px-3 mt-3 mb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={(e) => { if (item.disabled) e.preventDefault(); }}
                      data-testid={`${testid}-item-${(item.testid || item.label).toString().toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors relative min-h-[44px]',
                          item.disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'active:bg-[hsl(var(--surface-3))]',
                          isActive && !item.disabled
                            ? 'bg-[hsl(var(--surface-2))] text-foreground font-medium border-l-2 border-accent -ml-[2px] pl-[10px]'
                            : 'text-foreground/75 hover:bg-[hsl(var(--surface-2))] hover:text-foreground'
                        )
                      }
                    >
                      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[9px] uppercase tracking-wider h-4 px-1.5',
                            accent === 'admin'
                              ? 'border-accent/30 bg-accent/5 text-accent'
                              : 'border-accent/30 bg-accent/5 text-accent'
                          )}
                        >
                          {item.badge}
                        </Badge>
                      )}
                      {item.soon && (
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider h-4 px-1.5">
                          Soon
                        </Badge>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
};
